/*
 * SCSS → Tailwind token mapping (IntakeQuestionItem.scss deleted):
 *
 * .iqi-row:
 *   display:flex / align-items:center / gap:12px / padding:14px 16px
 *   → flex items-center gap-[12px] py-[14px] px-sp-2
 *   background:#ffffff / border:1px solid --border-card / border-radius:8px
 *   → bg-white border border-shade-10 rounded-large
 *
 * .iqi-drag-handle:
 *   display:flex / align-items:center / flex-shrink:0 / cursor:grab / padding:4px 2px
 *   → flex items-center shrink-0 cursor-grab py-1 px-0.5
 *
 * .iqi-drag-dots:
 *   width:14px / color:#253845B2 (text-medium at 70%)
 *   → w-[14px] text-text-light (text-text-light ≈ --text-medium)
 *
 * .iqi-type-icon-circle:
 *   display:flex / align-items:center / justify-content:center
 *   width:36px / height:36px / border-radius:50%
 *   background:--surface-secondary / flex-shrink:0 / color:--text-medium
 *   → flex items-center justify-center w-[36px] h-[36px] rounded-full bg-deep-navy-5 shrink-0 text-text-light
 *   (bg-deep-navy-5 = --surface-secondary)
 *
 * .iqi-text-icon (default icon fallback):
 *   font-size:14px / font-weight:600 / color:--text-medium
 *   → text-size-3 font-semibold text-text-light
 *
 * .iqi-content:
 *   flex:1 / display:flex / flex-direction:column / gap:6px / min-width:0
 *   → flex-1 flex flex-col gap-[6px] min-w-0
 *
 * .iqi-question-text: (word-break:break-word)
 *   → break-words (Tailwind class)
 *
 * .iqi-badges:
 *   display:flex / gap:6px / flex-wrap:wrap → flex gap-[6px] flex-wrap
 *
 * .iqi-actions:
 *   display:flex / align-items:center / gap:4px / flex-shrink:0
 *   → flex items-center gap-1 shrink-0
 *
 * .iqi-action-btn:
 *   display:flex / align-items:center / justify-content:center
 *   width:32px / height:32px / border:none / background:none
 *   border-radius:6px / cursor:pointer / color:--text-medium
 *   hover: background:--surface-secondary / color:--text-dark
 *   → flex items-center justify-center w-8 h-8 border-none bg-transparent
 *     rounded-md cursor-pointer text-text-light
 *     hover:bg-deep-navy-5 hover:text-text-dark
 *
 * Arbitrary values: gap-[12px], py-[14px], w-[36px], h-[36px], gap-[6px]
 * (no DS tokens at those exact sizes).
 */
import type { ReactNode } from 'react'
import {
  Badge,
  Label,
  TrashIcon,
  CopyIcon,
  EditIcon,
  EnvelopeIcon,
  BullhornIcon,
  ArrowsRepeatIcon,
  StethoscopeIcon,
  PrescriptionBottlePillIcon,
  ClipboardListIcon,
  PhoneIcon,
  CalendarDayIcon,
  ArrowUpFromBracketIcon,
} from '@quickmd-llc/design-system'
import {
  INTAKE_FORM_TYPE_EMAIL_TEXT,
  INTAKE_FORM_TYPE_STATEMENT_TEXT,
  INTAKE_FORM_TYPE_SINGLE_TEXT,
  INTAKE_FORM_TYPE_HEIGHT_WEIGHT_TEXT,
  INTAKE_FORM_TYPE_PREFERRED_PHARMACY_TEXT,
  INTAKE_FORM_TYPE_MULTIPLE_CHOICE_TEXT,
  INTAKE_FORM_TYPE_PHONE_NUMBER_TEXT,
  INTAKE_FORM_TYPE_DATE_TEXT,
  INTAKE_FORM_TYPE_FILE_UPLOAD_BOX_TEXT,
} from '../../../../../utils/constants'

type Props = {
  questionNumber: number
  type: string
  questionMess: string
  isRequired: boolean
  onEditSavedQuestion: () => void
  onDeleteQuestion: () => void
  onDuplicateQuestion?: (() => void) | undefined
}

function getTypeIcon(type: string): ReactNode {
  switch (type) {
    case INTAKE_FORM_TYPE_EMAIL_TEXT:
      return <EnvelopeIcon iconSize='s' />
    case INTAKE_FORM_TYPE_STATEMENT_TEXT:
      return <BullhornIcon iconSize='s' />
    case INTAKE_FORM_TYPE_SINGLE_TEXT:
      return <ArrowsRepeatIcon iconSize='s' />
    case INTAKE_FORM_TYPE_HEIGHT_WEIGHT_TEXT:
      return <StethoscopeIcon iconSize='s' />
    case INTAKE_FORM_TYPE_PREFERRED_PHARMACY_TEXT:
      return <PrescriptionBottlePillIcon iconSize='s' />
    case INTAKE_FORM_TYPE_MULTIPLE_CHOICE_TEXT:
      return <ClipboardListIcon iconSize='s' />
    case INTAKE_FORM_TYPE_PHONE_NUMBER_TEXT:
      return <PhoneIcon iconSize='s' />
    case INTAKE_FORM_TYPE_DATE_TEXT:
      return <CalendarDayIcon iconSize='s' />
    case INTAKE_FORM_TYPE_FILE_UPLOAD_BOX_TEXT:
      return <ArrowUpFromBracketIcon iconSize='s' />
    default:
      return <span className='text-size-3 font-semibold text-text-light'>T</span>
  }
}

const IntakeQuestionItem = ({
  questionNumber,
  type,
  questionMess,
  isRequired,
  onEditSavedQuestion,
  onDeleteQuestion,
  onDuplicateQuestion,
}: Props) => {
  return (
    <div className='flex items-center gap-[12px] rounded-large border border-shade-10 bg-white px-sp-2 py-[14px]'>
      <div className='flex h-[36px] w-[36px] shrink-0 items-center justify-center rounded-full bg-deep-navy-5 text-text-light'>
        {getTypeIcon(type)}
      </div>
      <div className='flex min-w-0 flex-1 flex-col gap-[6px]'>
        <Label size='md' className='break-words'>
          Q{questionNumber}. {questionMess}
        </Label>
        <div className='flex flex-wrap gap-[6px]'>
          {type ?
            <Badge variant='default' size='s'>
              {type}
            </Badge>
          : null}
          {isRequired ?
            <Badge variant='warning' size='s'>
              Required
            </Badge>
          : null}
        </div>
      </div>
      <div className='flex shrink-0 items-center gap-1'>
        <button
          className='flex h-8 w-8 cursor-pointer items-center justify-center rounded-md border-none bg-transparent text-text-light hover:bg-deep-navy-5 hover:text-text-dark'
          onClick={onDeleteQuestion}
          aria-label='Delete question'
          type='button'>
          <TrashIcon iconSize='s' />
        </button>
        {onDuplicateQuestion ?
          <button
            className='flex h-8 w-8 cursor-pointer items-center justify-center rounded-md border-none bg-transparent text-text-light hover:bg-deep-navy-5 hover:text-text-dark'
            onClick={onDuplicateQuestion}
            aria-label='Duplicate question'
            type='button'>
            <CopyIcon iconSize='s' />
          </button>
        : null}
        <button
          className='flex h-8 w-8 cursor-pointer items-center justify-center rounded-md border-none bg-transparent text-text-light hover:bg-deep-navy-5 hover:text-text-dark'
          onClick={onEditSavedQuestion}
          aria-label='Edit question'
          type='button'>
          <EditIcon iconSize='s' />
        </button>
      </div>
    </div>
  )
}

export { IntakeQuestionItem }
